// use-theme-sync — Resolves store.theme to effective light/dark and syncs to DOM.
//
// Responsibilities:
//   1. Read localStorage on init → hydrate store (before React paint)
//   2. Subscribe to store.theme → compute effective theme → set data-theme attribute
//   3. Listen to OS preference changes when theme === 'system'
//
// Session 42: Dark mode foundation. ui-slice already has ThemeMode / setTheme.

import { useEffect } from 'react';
import { useStore } from '../store/index.js';
import type { ThemeMode } from '../store/ui-slice.js';

const STORAGE_KEY = 'examforge-theme';

/** Resolve ThemeMode to effective 'light' | 'dark'. */
function resolveEffective(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  // jsdom in tests may lack matchMedia — treat as light.
  if (typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Read persisted theme from localStorage. Returns null if absent/invalid. */
export function readPersistedTheme(): ThemeMode | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // localStorage unavailable (SSR, privacy mode) — ignore.
  }
  return null;
}

/** Persist theme choice to localStorage. */
function persistTheme(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Ignore write failures.
  }
}

/** Apply effective theme to document root. */
function applyTheme(effective: 'light' | 'dark'): void {
  document.documentElement.dataset['theme'] = effective;
}

/**
 * Hook to keep DOM in sync with store.theme.
 * Call once at app root level (inside App component).
 */
export function useThemeSync(): void {
  const theme = useStore((s) => s['theme']);
  const setTheme = useStore((s) => s['setTheme']);

  // ── Init: hydrate from localStorage (runs once on mount) ──
  useEffect(() => {
    const persisted = readPersistedTheme();
    if (persisted !== null) {
      setTheme(persisted);
    }
    // Apply immediately to avoid flash of wrong theme.
    applyTheme(resolveEffective(persisted ?? theme));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync: store.theme → DOM (runs on every store change) ──
  useEffect(() => {
    const effective = resolveEffective(theme);
    applyTheme(effective);
    persistTheme(theme);
  }, [theme]);

  // ── OS preference change listener (only when theme === 'system') ──
  useEffect(() => {
    if (theme !== 'system') return;
    if (typeof window.matchMedia !== 'function') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      applyTheme(e.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [theme]);
}

/** Cycle: system → dark → light → system. For toggle button. */
export function nextThemeMode(current: ThemeMode): ThemeMode {
  if (current === 'system') return 'dark';
  if (current === 'dark') return 'light';
  return 'system';
}
